import { useCallback, useState } from 'react';
import { useSnackbar } from 'notistack';

import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractApiErrorMessage } from '../../shared/utils/extract-api-error-message.util';
import { ADMIN_INVOICES_API_PATHS } from '../constants/invoices-app-paths.constant';
import { InvoiceFormat } from '../types/invoice-format.type';
import { InvoiceTemplateId } from '../types/invoice-template-id.type';
import { DeliveryPointsScope } from '../types/delivery-points-scope.type';

export type CreateOrganizationInvoiceParams = {
  organizationId: string;
  templateId: InvoiceTemplateId;
  format: InvoiceFormat;
  from: string;
  to: string;
  deliveryPointsScope: DeliveryPointsScope;
  deliveryPointIds: string[];
};

type CreateOrganizationInvoiceResponse = {
  invoiceId: string;
  invoiceNumber: string;
  signedUrl: string;
};

export function useCreateOrganizationInvoice() {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);

  const createInvoice = useCallback(
    async (params: CreateOrganizationInvoiceParams): Promise<CreateOrganizationInvoiceResponse | null> => {
      setIsLoading(true);
      try {
        const url = ADMIN_INVOICES_API_PATHS.CREATE_ORGANIZATION_INVOICE(params.organizationId);
        const response = await HttpClient.post<CreateOrganizationInvoiceResponse>(url, {
          templateId: params.templateId,
          format: params.format,
          from: params.from,
          to: params.to,
          deliveryPointsScope: params.deliveryPointsScope,
          deliveryPointIds: params.deliveryPointIds,
        }, { timeout: 60000 });

        enqueueSnackbar(`Facture ${response.invoiceNumber} générée`, { variant: 'success' });
        window.open(response.signedUrl, '_blank', 'noopener,noreferrer');
        return response;
      } catch (error) {
        enqueueSnackbar(
          extractApiErrorMessage(error, 'Erreur lors de la génération de la facture'),
          { variant: 'error' },
        );
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [enqueueSnackbar],
  );

  return { createInvoice, isLoading };
}
